import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Suspense, useCallback, useEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Shell } from './app/Shell.js';
import { SessionProvider, useSession } from './app/session.js';
import { Loading } from './app/view.js';
import { VIEWS } from './app/views.js';
import { ModalProvider, useModal } from './components/Modal.js';
import { ToastProvider, useToast } from './components/Toast.js';
import { changePw, PwFields } from './features/akun/Akun.js';
import { Login } from './features/auth/Login.js';
import { SearchProvider } from './features/panduan/search.js';
import { api, onMustChangePassword } from './lib/api.js';
import type { Me } from './lib/session.js';

// Gerbang aplikasi: belum login → Login; sudah login → Shell per peran (pengganti boot() v1).

/** Akun dengan password sementara wajib ganti dulu (v1 A.forcePassword) — modal tanpa tombol tutup. */
function ForcePassword({ onDone }: { onDone: () => void }) {
  const { me } = useSession();
  const { open } = useModal();
  const toast = useToast();
  const shown = useRef(false);
  const show = useCallback(() => {
    if (shown.current) return;
    shown.current = true;
    open({
      title: 'Ganti password sementara',
      noClose: true,
      body: (
        <>
          <div id="forcePw" className="note">
            Demi keamanan, ganti password sementara Anda sebelum memakai aplikasi.
          </div>
          <PwFields username={me.username} />
        </>
      ),
      foot: (
        <button className="btn btn-primary" type="submit">
          Simpan &amp; lanjut
        </button>
      ),
      submit: async (f, m) => {
        await changePw(f);
        m.close();
        shown.current = false;
        toast('Password diganti.');
        onDone();
      },
    });
  }, [open, toast, onDone, me.username]);
  useEffect(() => {
    if (me.must_change_pw) show();
  }, [me.must_change_pw, show]);
  useEffect(() => onMustChangePassword(show), [show]);
  return null;
}

function Gate() {
  const qc = useQueryClient();
  // Nama usaha untuk halaman login & judul tab — dari Pengaturan (endpoint publik), bukan ditulis di kode.
  const brand = useQuery({
    queryKey: ['brand'],
    queryFn: () => api<{ business_name: string }>('GET', '/api/public/brand').catch(() => ({ business_name: '' })),
    staleTime: 5 * 60000,
  });
  useEffect(() => {
    const n = brand.data?.business_name;
    document.title = n ? `${n} — Penjualan` : 'Penjualan';
  }, [brand.data?.business_name]);
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return (await api<{ user: Me }>('GET', '/api/auth/me')).user;
      } catch {
        return null;
      }
    },
    staleTime: Number.POSITIVE_INFINITY,
  });
  const refreshMe = useCallback(() => void qc.invalidateQueries({ queryKey: ['me'] }), [qc]);
  const navigate = useNavigate();
  const loc = useLocation();
  const toast = useToast();
  // Setelah login: kembali ke halaman yang tadi dibuka (?next=, hanya alamat di aplikasi ini) atau ke beranda peran.
  const afterLogin = useCallback(async () => {
    const next = new URLSearchParams(window.location.search).get('next') || '';
    await qc.refetchQueries({ queryKey: ['me'] });
    navigate(/^\/(?!\/)/.test(next) && !next.startsWith('/login') ? next : '/', { replace: true });
  }, [qc, navigate]);
  // Keluar (atau sesi berakhir): kembali ke halaman login /login — login berikutnya mulai dari beranda peran itu.
  const loggedOut = useCallback(
    (msg?: string) => {
      qc.setQueryData(['me'], null);
      document.body.classList.remove('is-home');
      delete document.body.dataset.view;
      navigate('/login', { replace: true });
      if (msg) toast(msg);
    },
    [qc, navigate, toast],
  );

  // Sama dengan v1: sebelum status login diketahui, layar kosong (tidak berkedip ke login).
  if (me.isPending) return null;
  if (!me.data) {
    // Belum login → halaman login selalu di /login (alamat yang dibuka diingat lewat ?next=).
    if (loc.pathname !== '/login') {
      const here = loc.pathname + loc.search;
      return <Navigate replace to={here === '/' ? '/login' : `/login?next=${encodeURIComponent(here)}`} />;
    }
    return <Login brand={brand.data?.business_name || ''} onLoggedIn={() => void afterLogin()} />;
  }
  return (
    <SessionProvider me={me.data} onLogout={loggedOut}>
      {/* Modal di dalam sesi: isi modal boleh memakai useSession() (React context mengikuti pohon komponen, bukan DOM). */}
      <ModalProvider>
        <ForcePassword onDone={refreshMe} />
        <SearchProvider>
          <Suspense fallback={<Loading />}>
            <Shell views={VIEWS} />
          </Suspense>
        </SearchProvider>
      </ModalProvider>
    </SessionProvider>
  );
}

export function App() {
  return (
    <ToastProvider>
      <Gate />
    </ToastProvider>
  );
}
